import { useEffect, useSyncExternalStore, useCallback } from 'react'
import { CloudOff, RefreshCw, AlertCircle } from 'lucide-react'
import { useAuth } from '@pilot-hub/auth'
import { useBestaetigung } from '@pilot-hub/ui'
import { getConnectionState, subscribeConnection } from '@pilot-hub/api-client'
import { queueLesen, queueAbonnieren, queueSenden, queueVerwerfen, beschreibe, fehlerLesen, fehlerLeeren, type QueueEintrag } from '../utils/flugQueue'

/** Reaktiver Stand der Warteschlange (Liste ist unveränderlich, ändert sich nur bei Schreibzugriffen) */
export function useFlugQueue(): QueueEintrag[] {
  return useSyncExternalStore(queueAbonnieren, queueLesen, queueLesen)
}

/** Sendet die Warteschlange bei Wiederverbindung (online-Event, Erfolg im Connection-Store) und periodisch. */
export function useFlugQueueSync() {
  const { user } = useAuth()
  const nutzerId = user?.id
  const senden = useCallback(() => {
    if (nutzerId && queueLesen().some(e => e.nutzerId === nutzerId)) void queueSenden(nutzerId)
  }, [nutzerId])

  useEffect(() => {
    senden()
    const onOnline = () => senden()
    window.addEventListener('online', onOnline)
    let letzter = getConnectionState().status
    const unsub = subscribeConnection(() => {
      const s = getConnectionState().status
      if (s === 'ok' && letzter !== 'ok') senden()
      letzter = s
    })
    const t = setInterval(() => { if (document.visibilityState !== 'hidden') senden() }, 20000)
    return () => { window.removeEventListener('online', onOnline); unsub(); clearInterval(t) }
  }, [senden])
}

/** Hinweis in der Statuszeile (Banner-Slot): ausstehende Aktionen und abgelehnte Aktionen */
export function FlugQueueHinweis() {
  const { user } = useAuth()
  const liste = useFlugQueue()
  useFlugQueueSync()
  const meine = liste.filter(e => e.nutzerId === user?.id)
  const haengend = meine.filter(e => e.letzterFehler)
  const { bestaetigen, dialog } = useBestaetigung()
  const verwerfen = async (e: QueueEintrag) => {
    const ok = await bestaetigen({ titel: 'Aktion verwerfen?', text: 'Nicht gesendet:', objekt: beschreibe(e, liste), hinweis: 'Die Angaben gehen verloren und müssen manuell nachgetragen werden.', bestaetigenText: 'Verwerfen' })
    if (ok) queueVerwerfen(e.qid)
  }
  const fehler = fehlerLesen()
  if (meine.length === 0 && fehler.length === 0) return null
  return (
    <div className="space-y-1" data-testid="flug-queue-hinweis">
      {dialog}
      {meine.length > 0 && (
        <div role="status" className="flex items-center gap-2 border-b border-amber-500/30 bg-amber-500/10 px-4 py-2 text-sm text-amber-200">
          <CloudOff className="h-4 w-4 shrink-0" />
          <span className="min-w-0 flex-1">
            {meine.length} {meine.length === 1 ? 'Aktion' : 'Aktionen'} offline gespeichert – wird gesendet, sobald Verbindung besteht.
          </span>
          <button type="button" onClick={() => void queueSenden(user?.id, true)}
            className="flex min-h-[44px] shrink-0 items-center gap-1.5 rounded-lg px-3 font-medium hover:bg-amber-500/20">
            <RefreshCw className="h-4 w-4" />Jetzt senden
          </button>
        </div>
      )}
      {haengend.map(e => (
        <div key={e.qid} role="alert" data-testid="flug-queue-haengend" className="flex flex-wrap items-start gap-2 border-b border-red-500/30 bg-red-500/10 px-4 py-2 text-sm text-red-200">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span className="min-w-0 basis-[calc(100%-2rem)] flex-1">
            {beschreibe(e, liste)} nicht gesendet: {e.letzterFehler}. Bleibt gespeichert, wird erneut versucht.
          </span>
          <button type="button" onClick={() => void queueSenden(user?.id, true)} className="min-h-[44px] shrink-0 rounded-lg px-3 font-medium hover:bg-red-500/20">Erneut senden</button>
          <button type="button" data-testid="flug-queue-verwerfen"
            onClick={() => void verwerfen(e)}
            className="min-h-[44px] shrink-0 rounded-lg px-3 font-medium hover:bg-red-500/20">Verwerfen</button>
        </div>
      ))}
      {fehler.length > 0 && (
        <div role="alert" className="flex items-start gap-2 border-b border-red-500/30 bg-red-500/10 px-4 py-2 text-sm text-red-200">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          <span className="min-w-0 flex-1">{fehler.map(f => f.text).join(' · ')}</span>
          <button type="button" onClick={fehlerLeeren} className="min-h-[44px] shrink-0 rounded-lg px-3 font-medium hover:bg-red-500/20">OK</button>
        </div>
      )}
    </div>
  )
}

/** Anzahl ausstehender Flug-Aktionen des angemeldeten Nutzers (für das Abzeichen in der StatusBar) */
export function useFlugQueueAnzahl(): number {
  const { user } = useAuth()
  const liste = useFlugQueue()
  return liste.filter(e => e.nutzerId === user?.id).length
}
